// Sources/Brands configuration for source column
export interface SourceInfo {
  fa: string;
  en: string;
  icon: string;
  image: string | null;
}

export const sources: Record<string, SourceInfo> = {
  "smart": {
    fa: "هوشمند",
    en: "Smart",
    icon: "🧠",
    image: null
  },
  "google": {
    fa: "گوگل",
    en: "Google",
    icon: "🔍",
    image: "https://www.google.com/s2/favicons?domain=google.com&sz=32"
  },
  "bing": {
    fa: "بینگ",
    en: "Bing",
    icon: "🔎",
    image: "https://www.google.com/s2/favicons?domain=bing.com&sz=32"
  },
  "youtube": {
    fa: "یوتیوب",
    en: "YouTube",
    icon: "▶️",
    image: "https://www.google.com/s2/favicons?domain=youtube.com&sz=32"
  },
  "aparat": {
    fa: "آپارات",
    en: "Aparat",
    icon: "📹",
    image: "https://www.google.com/s2/favicons?domain=aparat.com&sz=32"
  },
  "digikala": {
    fa: "دیجی‌کالا",
    en: "Digikala",
    icon: "🛒",
    image: "https://www.google.com/s2/favicons?domain=digikala.com&sz=32"
  },
  "divar": {
    fa: "دیوار",
    en: "Divar",
    icon: "🏠",
    image: "https://www.google.com/s2/favicons?domain=divar.ir&sz=32"
  },
  "instagram": {
    fa: "اینستاگرام",
    en: "Instagram",
    icon: "📷",
    image: "https://www.google.com/s2/favicons?domain=instagram.com&sz=32"
  },
  "ai": {
    fa: "هوش مصنوعی",
    en: "AI",
    icon: "🤖",
    image: null
  }
};

// Function to get source info
export const getSourceInfo = (sourceType: string): SourceInfo | null => {
  return sources[sourceType] || null;
};

// Function to get difficulty color and label
export interface DifficultyStyle {
  color: string;
  label: string;
  bgColor: string;
}

export const getDifficultyStyle = (diff: number | null | undefined): DifficultyStyle | null => {
  if (diff === null || diff === undefined) {
    return null;
  }

  if (diff >= 0 && diff < 15) {
    return { color: '#008000', label: 'آسان', bgColor: '#dcfce7' };
  } else if (diff >= 15 && diff < 30) {
    return { color: '#10b981', label: 'نسبتا آسان', bgColor: '#d1fae5' };
  } else if (diff >= 30 && diff < 50) {
    return { color: '#dfba03', label: 'امکان پذیر', bgColor: '#fef3c7' };
  } else if (diff >= 50 && diff < 70) {
    return { color: '#f97316', label: 'سخت', bgColor: '#fed7aa' };
  } else if (diff >= 70 && diff < 85) {
    return { color: '#ef4444', label: 'بسیار سخت', bgColor: '#fee2e2' };
  } else {
    return { color: '#b50000', label: 'تقریباً ناممکن', bgColor: '#fecaca' };
  }
};

// Difficulty Ranges for Tooltip
export interface DifficultyRange {
  min: number;
  max: number;
  label: string;
  enLabel: string;
  color: string;
  bgColor: string;
}

export const difficultyRanges: DifficultyRange[] = [
  { min: 0, max: 14, label: 'آسان', enLabel: 'Easy', color: '#008000', bgColor: '#dcfce7' },
  { min: 15, max: 29, label: 'نسبتا آسان', enLabel: 'Fairly Easy', color: '#10b981', bgColor: '#d1fae5' },
  { min: 30, max: 49, label: 'امکان پذیر', enLabel: 'Possible', color: '#dfba03', bgColor: '#fef3c7' },
  { min: 50, max: 69, label: 'سخت', enLabel: 'Hard', color: '#f97316', bgColor: '#fed7aa' },
  { min: 70, max: 84, label: 'بسیار سخت', enLabel: 'Very Hard', color: '#ef4444', bgColor: '#fee2e2' },
  { min: 85, max: 100, label: 'تقریباً ناممکن', enLabel: 'Nearly Impossible', color: '#b50000', bgColor: '#fecaca' }
];

// Intent Types Configuration
export interface IntentType {
  label: string;
  enLabel: string;
  abbreviation: string;
  enAbbreviation: string;
  color: string;
  bgColor: string;
  description: {
    fa: string;
    en: string;
  };
}

export const intentTypes: Record<string, IntentType> = {
  informational: {
    label: 'اطلاعاتی',
    enLabel: 'Info',
    abbreviation: 'اطلاعاتی',
    enAbbreviation: 'Info',
    color: '#1e40af',
    bgColor: '#dbeafe',
    description: {
      fa: 'کاربر به دنبال اطلاعات درباره یک موضوع خاص است. مثال: "چگونه یک ماشین دنده اتومات تعمیر کنیم؟" "فواید ورزش برای سلامتی."',
      en: 'The user is looking for information about a specific topic. Example: "How to repair an automatic transmission car?" "Benefits of exercise for health."'
    }
  },
  navigational: {
    label: 'هدایتی',
    enLabel: 'Nav',
    abbreviation: 'هدایتی',
    enAbbreviation: 'Nav',
    color: '#059669',
    bgColor: '#d1fae5',
    description: {
      fa: 'کاربر به دنبال دسترسی به یک وب‌سایت یا موضوع خاص است. مثال: جستجوی "Instagram" برای رفتن به وب‌سایت اینستاگرام. جستجو درباره موضوعی خاص مثل "وردپرس".',
      en: 'The user is looking to access a specific website or topic. Example: Searching "Instagram" to go to Instagram website. Searching for a specific topic like "WordPress".'
    }
  },
  commercial: {
    label: 'تجاری',
    enLabel: 'Comm',
    abbreviation: 'تجاری',
    enAbbreviation: 'Comm',
    color: '#d97706',
    bgColor: '#fef3c7',
    description: {
      fa: 'این نوع جستجو ترکیبی است و کاربر هنوز تصمیم نگرفته است، اما در حال بررسی گزینه‌ها است. مثال: "بهترین گوشی‌های 2025." "مقایسه پژو 206 و تیبا."',
      en: 'This type of search is mixed and the user has not yet decided, but is reviewing options. Example: "Best phones 2025." "Comparison of Peugeot 206 and Tiba."'
    }
  },
  transactional: {
    label: 'تراکنشی',
    enLabel: 'Trans',
    abbreviation: 'تراکنشی',
    enAbbreviation: 'Trans',
    color: '#dc2626',
    bgColor: '#fee2e2',
    description: {
      fa: 'کاربر قصد خرید یا انجام یک اقدام خاص را دارد. مثال: "خرید لپ‌تاپ اپل." "رزرو هتل در کیش."',
      en: 'The user intends to purchase or perform a specific action. Example: "Buy Apple laptop." "Book hotel in Kish."'
    }
  }
};

// Function to get intent style
export const getIntentStyle = (intentType: string): IntentType | null => {
  return intentTypes[intentType] || null;
};

// Purpose Types Configuration
export interface PurposeType {
  label: string;
  enLabel: string;
  icon: string;
  color: string;
  bgColor: string;
  description: {
    fa: string;
    en: string;
  };
}

export const purposeTypes: Record<string, PurposeType> = {
  'Grouping': {
    label: 'دسته‌بندی',
    enLabel: 'Grouping',
    icon: '📁',
    color: '#6366f1',
    bgColor: '#e0e7ff',
    description: {
      fa: 'دسته‌بندی و گروه‌بندی محتوا',
      en: 'Content categorization and grouping'
    }
  },
  'local': {
    label: 'محلی',
    enLabel: 'Local',
    icon: '📍',
    color: '#10b981',
    bgColor: '#d1fae5',
    description: {
      fa: 'محتوای مرتبط با موقعیت جغرافیایی',
      en: 'Geographic location related content'
    }
  },
  'Product': {
    label: 'محصول',
    enLabel: 'Product',
    icon: '📦',
    color: '#f59e0b',
    bgColor: '#fef3c7',
    description: {
      fa: 'صفحات محصولات و کالاها',
      en: 'Product and goods pages'
    }
  },
  'Tag or Filter': {
    label: 'برچسب یا فیلتر',
    enLabel: 'Tag or Filter',
    icon: '🏷️',
    color: '#8b5cf6',
    bgColor: '#ede9fe',
    description: {
      fa: 'برچسب‌گذاری و فیلتر کردن محتوا',
      en: 'Content tagging and filtering'
    }
  },
  'Brand': {
    label: 'برند',
    enLabel: 'Brand',
    icon: '⭐',
    color: '#ec4899',
    bgColor: '#fce7f3',
    description: {
      fa: 'صفحات برند و معرفی شرکت',
      en: 'Brand and company introduction pages'
    }
  },
  'Article': {
    label: 'مقاله',
    enLabel: 'Article',
    icon: '📄',
    color: '#3b82f6',
    bgColor: '#dbeafe',
    description: {
      fa: 'مقالات و محتوای متنی',
      en: 'Articles and text content'
    }
  },
  'Service': {
    label: 'خدمات',
    enLabel: 'Service',
    icon: '🔧',
    color: '#14b8a6',
    bgColor: '#ccfbf1',
    description: {
      fa: 'صفحات خدمات و سرویس‌ها',
      en: 'Service and services pages'
    }
  },
  'Site Overview': {
    label: 'سایر',
    enLabel: 'Site Overview',
    icon: '🌐',
    color: '#64748b',
    bgColor: '#f1f5f9',
    description: {
      fa: 'سایر صفحات و محتواهای سایت',
      en: 'Other site pages and content'
    }
  }
};

// Function to get purpose style
export const getPurposeStyle = (purposeType: string): PurposeType | null => {
  return purposeTypes[purposeType] || null;
};

// Format search volume number
export const formatSearchVolume = (value: number | null | undefined): string => {
  if (!value || value === 0) return '-';
  
  if (value >= 1000000) {
    const formatted = (value / 1000000).toFixed(1);
    return formatted.endsWith('.0') ? formatted.replace('.0', '') + 'M' : formatted + 'M';
  } else if (value >= 1000) {
    const formatted = (value / 1000).toFixed(1);
    return formatted.endsWith('.0') ? formatted.replace('.0', '') + 'K' : formatted + 'K';
  } else {
    return value.toLocaleString();
  }
};

// Get search volume color and percentage for chart
export interface SearchVolumeStyle {
  color: string;
  percentage: number;
}

export const getSearchVolumeStyle = (value: number | null | undefined): SearchVolumeStyle => {
  if (!value || value === 0) return { color: '#6b7280', percentage: 0 };
  
  // Assuming max volume is 100000 for percentage calculation
  const maxVolume = 100000;
  const percentage = Math.min((value / maxVolume) * 100, 100);
  
  if (value >= 100000) {
    return { color: '#059669', percentage: 100 }; // High volume - Green
  } else if (value >= 10000) {
    return { color: '#0284c7', percentage: percentage }; // Medium-High - Blue
  } else if (value >= 1000) {
    return { color: '#7c3aed', percentage: percentage }; // Medium - Purple
  } else {
    return { color: '#dc2626', percentage: percentage }; // Low - Red
  }
};

// Helper function to lighten a hex color
export const lightenColor = (hex: string, percent: number = 30): string => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten each component
  const newR = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));
  
  // Convert back to hex
  const toHex = (n: number): string => {
    const hex = n.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

